import React, { useMemo, useState } from "react";
import { Calendar, ChevronDown, ChevronUp, Image, Video, Link2 } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";
import { useCampaignDays } from "../../hooks/useCampaignDays";
import { format, addDays, parseISO } from "date-fns";
import { ar, enUS } from "date-fns/locale";
import { ScrollArea, ScrollBar } from "@/components/ui/scroll-area";
import type { Creative, Location } from "../../types";

interface CreativeCalendarOverviewProps {
  locationCreatives: Record<string, Creative[]>;
  selectedLocations: Location[];
  mediaOwnerCreatives?: Record<string, Creative[]>;
}

const CreativeCalendarOverview = ({
  locationCreatives,
  selectedLocations,
  mediaOwnerCreatives = {},
}: CreativeCalendarOverviewProps) => {
  const { language } = useLanguage();
  const { totalDays } = useCampaignDays();
  const [isExpanded, setIsExpanded] = useState(true);
  const isRtl = language === "ar";
  const locale = isRtl ? ar : enUS;

  // Get campaign dates
  const campaignDates = useMemo(() => {
    const storedStartDate = localStorage.getItem("startDate");
    const start = storedStartDate ? parseISO(storedStartDate) : new Date();
    
    const dates: Date[] = [];
    for (let i = 0; i < totalDays; i++) {
      dates.push(addDays(start, i));
    }
    return dates;
  }, [totalDays]);

  // Combine all creatives into a flat list
  const allCreatives = useMemo(() => {
    const creatives: Creative[] = [];
    Object.values(locationCreatives).forEach(arr => creatives.push(...arr));
    Object.values(mediaOwnerCreatives).forEach(arr => creatives.push(...arr));
    return creatives;
  }, [locationCreatives, mediaOwnerCreatives]);

  // Check if any location has 2+ creatives (only then is scheduling needed)
  const hasMultipleCreativesPerLocation = useMemo(() => {
    // Check location-specific creatives
    for (const creatives of Object.values(locationCreatives)) {
      if (creatives.length >= 2) return true;
    }
    // Check media owner creatives grouped by location
    for (const creatives of Object.values(mediaOwnerCreatives)) {
      // Group by location name
      const byLocation: Record<string, number> = {};
      creatives.forEach(c => {
        const loc = c.locationName || 'default';
        byLocation[loc] = (byLocation[loc] || 0) + 1;
      });
      if (Object.values(byLocation).some(count => count >= 2)) return true;
    }
    // If only one location and total creatives >= 2
    if (selectedLocations.length === 1 && allCreatives.length >= 2) return true;
    return false;
  }, [locationCreatives, mediaOwnerCreatives, selectedLocations, allCreatives]);

  // Get creatives for a specific day
  const getCreativesForDay = (dayNumber: number) => {
    return allCreatives.filter(c => c.scheduledDays?.includes(dayNumber));
  };

  // Don't show if:
  // - No creatives
  // - Single day campaign
  // - No location has 2+ creatives (scheduling not needed)
  if (allCreatives.length === 0 || totalDays <= 1 || !hasMultipleCreativesPerLocation) {
    return null;
  }

  const getCreativeIcon = (creative: Creative) => {
    if (creative.isExternalLink) return Link2;
    if (creative.type.startsWith('video')) return Video;
    return Image;
  };

  return (
    <div className="border rounded-lg bg-card overflow-hidden">
      {/* Header */}
      <button 
        type="button"
        className="w-full flex items-center justify-between p-3 hover:bg-muted/50 transition-colors"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex items-center gap-2">
          <Calendar className="h-4 w-4 text-primary" />
          <span className="text-sm font-medium">
            {isRtl ? 'جدول المحتوى' : 'Schedule Timeline'}
          </span>
          <span className="text-xs text-muted-foreground">
            ({allCreatives.length} {isRtl ? 'ملف' : 'file'}{allCreatives.length !== 1 && !isRtl ? 's' : ''})
          </span>
        </div>
        {isExpanded ? (
          <ChevronUp className="h-4 w-4 text-muted-foreground" />
        ) : (
          <ChevronDown className="h-4 w-4 text-muted-foreground" />
        )}
      </button>

      {/* Timeline */}
      {isExpanded && (
        <div className="px-3 pb-3">
          <ScrollArea className="w-full">
            <div className="flex gap-2 min-w-max">
              {campaignDates.map((date, idx) => {
                const dayNumber = idx + 1;
                const dayCreatives = getCreativesForDay(dayNumber);
                const hasContent = dayCreatives.length > 0;

                return (
                  <div 
                    key={idx}
                    className={`
                      flex-shrink-0 w-24 rounded-lg border p-2 text-center
                      ${hasContent 
                        ? 'bg-green-50 border-green-200 dark:bg-green-900/20 dark:border-green-800' 
                        : 'bg-muted/30 border-dashed'
                      }
                    `}
                  >
                    {/* Date header */}
                    <div className="text-[10px] text-muted-foreground uppercase">
                      {format(date, 'EEE', { locale })}
                    </div>
                    <div className="text-sm font-semibold">
                      {format(date, 'MMM d', { locale })}
                    </div>

                    {/* Content indicator */}
                    <div className="mt-2 min-h-[28px] flex items-center justify-center">
                      {hasContent ? (
                        <div className="flex items-center gap-1">
                          {dayCreatives.slice(0, 2).map((c, i) => {
                            const Icon = getCreativeIcon(c);
                            return <Icon key={i} className="h-4 w-4 text-green-600 dark:text-green-400" />;
                          })}
                          {dayCreatives.length > 2 && (
                            <span className="text-xs text-green-600 dark:text-green-400 font-medium">
                              +{dayCreatives.length - 2}
                            </span>
                          )}
                        </div>
                      ) : (
                        <span className="text-xs text-muted-foreground/50">—</span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
            <ScrollBar orientation="horizontal" />
          </ScrollArea>
        </div>
      )}
    </div>
  );
};

export default CreativeCalendarOverview;
